// 🤖 API Craft Back Office - Chat Message Component
import React, { useState } from 'react';
import { ChatMessage as ChatMessageType } from '../types';

interface ChatMessageProps {
  message: ChatMessageType;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const [showDetails, setShowDetails] = useState(false);

  const formatTime = (timestamp: Date) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatContent = (content: string) => {
    // Simple markdown-like formatting for lists
    return content
      .split('\n')
      .map((line, index) => {
        if (line.startsWith('• ')) {
          return (
            <li key={index} className="ml-4 mb-1">
              {line.substring(2)}
            </li>
          );
        }
        if (line.match(/^\d+\./)) {
          return (
            <li key={index} className="ml-4 mb-1 list-decimal">
              {line.replace(/^\d+\.\s*/, '')}
            </li>
          );
        }
        if (line.trim() === '') {
          return <br key={index} />;
        }
        return (
          <p key={index} className="mb-2">
            {line}
          </p>
        );
      });
  };

  return (
    <div className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} mb-4`}>
      <div className={`max-w-[80%] ${
        message.type === 'user' 
          ? 'bg-mint-500 text-white rounded-l-2xl rounded-tr-2xl' 
          : 'bg-white border border-sage-200 rounded-r-2xl rounded-tl-2xl shadow-sm'
      } p-4`}>
        
        {/* Message Content */}
        <div className={`prose prose-sm max-w-none ${
          message.type === 'user' ? 'prose-invert' : ''
        }`}>
          {formatContent(message.content)}
        </div>

        {/* Message Metadata */}
        <div className={`flex items-center justify-between mt-3 pt-2 border-t ${
          message.type === 'user' 
            ? 'border-mint-400 text-mint-100' 
            : 'border-sage-100 text-sage-500'
        } text-xs`}>
          
          <span>{formatTime(message.timestamp)}</span>
          
          {message.type === 'assistant' && (message.tokens_used || message.execution_time_ms) && (
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center space-x-1 hover:text-sage-700 transition-colors"
            >
              <span>🔍</span>
              <span>Details</span>
            </button>
          )}
        </div>

        {/* Technical Details (AI Messages Only) */}
        {showDetails && message.type === 'assistant' && (
          <div className="mt-3 pt-3 border-t border-sage-100 text-xs space-y-2">
            
            {message.tokens_used && (
              <div className="flex justify-between">
                <span className="text-sage-600">Tokens:</span>
                <span className="font-mono">{message.tokens_used}</span>
              </div>
            )}
            
            {message.execution_time_ms && (
              <div className="flex justify-between">
                <span className="text-sage-600">Time:</span>
                <span className="font-mono">{message.execution_time_ms.toFixed(1)}ms</span>
              </div>
            )}
            
            {message.generated_sql && (
              <div className="mt-2">
                <div className="text-sage-600 mb-1">Generated SQL:</div>
                <pre className="bg-sage-50 p-2 rounded text-xs overflow-x-auto font-mono">
                  {message.generated_sql}
                </pre>
              </div>
            )}

            {message.query_results && message.query_results.length > 0 && (
              <div className="mt-2">
                <div className="text-sage-600 mb-1">Results ({message.query_results.length}):</div>
                <pre className="bg-sage-50 p-2 rounded text-xs overflow-x-auto max-h-32 font-mono">
                  {JSON.stringify(message.query_results, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatMessage;
